import { memo, useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent } from 'components/ui/dialog';
import { useNotificationFromDBContext, useUserContext } from '@app/providers';
import {
  getNotificationsForUser,
  getUserById,
  signIn,
  SignInData,
  signInByService,
  setToken,
} from '@app/api';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import { parseJwt } from '@app/helpers';
import {
  RestorePasswordForm,
  SignInForm,
  SignInNewForm,
  RegisterNewForm,
} from '@app/forms';
import bgImage from './bg.jpeg';

interface SignInModalProps {
  visible: boolean;
  className?: string;
  initialUseNewUI?: boolean;
}

function SignInModal(props: SignInModalProps) {
  const { visible, className = '', initialUseNewUI = false } = props;
  const navigate = useNavigate();
  const [pending, setPending] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [useNewUI, setUseNewUI] = useState(initialUseNewUI);
  const [isRegisterMode, setIsRegisterMode] = useState(false); // For new register form
  const { setUser, hideSignInModal } = useUserContext();
  const { setNotifications } = useNotificationFromDBContext();

  // Update useNewUI when initialUseNewUI changes
  useEffect(() => {
    setUseNewUI(initialUseNewUI);
  }, [initialUseNewUI]);

  // Prevent iOS Safari zoom on input focus and reset on close
  useEffect(() => {
    const meta = document.querySelector(
      'meta[name="viewport"]'
    ) as HTMLMetaElement | null;
    const prev = meta?.getAttribute('content') || '';

    if (visible && meta) {
      meta.setAttribute(
        'content',
        'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover'
      );
    }

    if (!visible) {
      // Ensure no focused element keeps the page zoomed
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      // Small timeout to allow dialog close animation to finish before restore
      setTimeout(() => {
        if (meta) {
          meta.setAttribute(
            'content',
            prev || 'width=device-width, initial-scale=1'
          );
        }
        // Nudge scroll to reset any accidental zoom offset
        window.scrollTo(0, Math.max(window.scrollY - 1, 0));
        window.scrollTo(0, Math.max(window.scrollY + 1, 0));
      }, 50);
    }

    return () => {
      if (meta) {
        meta.setAttribute(
          'content',
          prev || 'width=device-width, initial-scale=1'
        );
      }
    };
  }, [visible]);

  const onClickSignUp = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      if (pending) return;
      hideSignInModal();
      navigate('/sign-up');
    },
    [navigate, pending, hideSignInModal]
  );

  const handleSuccessfulAuth = useCallback(
    async (accessToken: string) => {
      Cookies.set('accessToken', accessToken);
      setToken(accessToken);

      const user = await getUserById(parseJwt(accessToken).id);
      setUser({ authenticated: true, ...user });

      const notifications = await getNotificationsForUser({ isRead: false });
      setNotifications(notifications);

      hideSignInModal();
      navigate('/');
    },
    [hideSignInModal, navigate, setNotifications, setUser]
  );

  const onSubmit = useCallback(
    async (data: SignInData): Promise<boolean> => {
      if (pending) return false;

      try {
        setPending(true);
        const { accessToken } = await signIn(data);
        await handleSuccessfulAuth(accessToken);
        return true;
      } catch (e) {
        throw e;
      } finally {
        setPending(false);
      }
    },
    [pending, handleSuccessfulAuth]
  );

  const onServiceSignIn = useCallback(
    async (data: { phone: string; code: string }) => {
      if (pending) return;

      try {
        setPending(true);
        const { accessToken } = await signInByService(data);
        await handleSuccessfulAuth(accessToken);
      } finally {
        setPending(false);
      }
    },
    [pending, handleSuccessfulAuth]
  );

  const onCurrentStepHandler = useCallback((step: number) => {
    setCurrentStep(step);
  }, []);

  const handleRegisterClick = useCallback(() => {
    setIsRegisterMode(true);
  }, []);

  const handleSignInClick = useCallback(() => {
    setIsRegisterMode(false);
  }, []);

  const getTitle = () => {
    if (useNewUI) {
      return isRegisterMode ? 'Регистрация' : 'Войти';
    }
    return 'Войти';
  };

  return (
    <Dialog
      open={visible}
      onOpenChange={(open) => {
        if (!open) hideSignInModal();
      }}
    >
      <DialogContent
        className={`max-w-md p-0 max-h-[90vh] min-h-0 overflow-hidden flex flex-col ${className}`.trim()}
      >
        <div className="relative h-[70px] w-full flex-shrink-0">
          <img
            src={bgImage}
            alt="background"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40" />
          <div className="absolute inset-0 flex items-center justify-between px-4">
            <span className="text-white text-lg font-semibold drop-shadow">
              {getTitle()}
            </span>
            <button
              onClick={() => setUseNewUI(!useNewUI)}
              className="text-white text-xs underline drop-shadow"
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              {useNewUI ? 'Old UI' : 'New UI'}
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto flex-1 min-h-0">
          {useNewUI ? (
            isRegisterMode ? (
              <RegisterNewForm
                onSuccess={handleSuccessfulAuth}
                onClickSignIn={handleSignInClick}
              />
            ) : (
              <SignInNewForm
                onSuccess={handleSuccessfulAuth}
                onClickRegister={handleRegisterClick}
              />
            )
          ) : (
            <>
              {currentStep === 2 ? (
                <RestorePasswordForm
                  onSubmit={onSubmit}
                  pending={pending}
                  onCurrentStepHandler={onCurrentStepHandler}
                  currentStep={currentStep}
                />
              ) : (
                <SignInForm
                  onSubmit={onSubmit}
                  onServiceSignIn={onServiceSignIn}
                  pending={pending}
                  onClickSignUp={onClickSignUp}
                  onCurrentStepHandler={onCurrentStepHandler}
                  currentStep={currentStep}
                  onCancel={hideSignInModal}
                />
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default memo(SignInModal);
